export type Endpoint = {
  method: "GET" | "POST" | "PATCH" | "DELETE";
  path: string;
  summary: string;

  auth: "public" | "user" | "service";
  body?: string;
  returns: string;
  notes?: string;
};

export type Section = {
  id: string;
  title: string;
  intro: string;
  endpoints?: Endpoint[];
  events?: { name: string; direction: "in" | "out"; payload: string; summary: string }[];
  code?: { caption: string; language: string; source: string }[];
};

export const OVERVIEW = `BS Meet is two processes and a media server. The API and the
WebSocket live on one origin, the browser client on another, and a mediasoup SFU
carries the audio and video. Everything below is the boundary between them.

Anything that happens before a meeting - signing in, creating a room, scheduling,
inviting - is REST. Everything inside a meeting is one WebSocket. There is no
polling, and no second channel.`;

export const SECTIONS: Section[] = [
  {
    id: "auth",
    title: "Authentication",
    intro: `Three credentials with three lifetimes. An access token is a short-lived
JWT sent as \`Authorization: Bearer\`. A refresh token is opaque, rotates on every
use, and is stored only as a keyed hash. Presenting one that has already been
spent signs out the whole session, that device and every token in its chain,
because a token used twice has two holders and only one of them is you. Two
requests racing with the same token, which is what two open tabs do, are
allowed for a few seconds after the rotation rather than being punished. A join token is minted by an
integrating service for one of its users and is good for one room for five minutes.

Guests present no credential at all. They send a name on the socket and the room's
settings decide the rest.`,
    endpoints: [
      {
        method: "POST",
        path: "/api/auth/register",
        summary: "Create an account.",
        auth: "public",
        body: `{ "email": string, "password": string, "displayName": string }`,
        returns: `{ accessToken, refreshToken, expiresIn, user }`,
      },
      {
        method: "POST",
        path: "/api/auth/login",
        summary: "Exchange an email and password for a session.",
        auth: "public",
        body: `{ "email": string, "password": string }`,
        returns: `{ accessToken, refreshToken, expiresIn, user }`,
      },
      {
        method: "POST",
        path: "/api/auth/refresh",
        summary: "Rotate a refresh token.",
        auth: "public",
        body: `{ "refreshToken": string }`,
        returns: `{ accessToken, refreshToken, expiresIn, user }`,
        notes: "The token presented stops working the moment this returns.",
      },
      {
        method: "POST",
        path: "/api/auth/logout",
        summary: "Revoke one device session.",
        auth: "public",
        body: `{ "refreshToken": string }`,
        returns: "204",
      },
      {
        method: "GET",
        path: "/api/auth/me",
        summary: "The signed-in account.",
        auth: "user",
        returns: "User",
      },
      {
        method: "PATCH",
        path: "/api/auth/me",
        summary: "Change the display name or avatar.",
        auth: "user",
        body: `{ "displayName"?: string, "avatarUrl"?: string | null }`,
        returns: "User",
        notes: "An absent key leaves the field alone; an explicit one replaces it.",
      },
      {
        method: "POST",
        path: "/api/auth/password",
        summary: "Change the password.",
        auth: "user",
        body: `{ "currentPassword": string, "newPassword": string }`,
        returns: "204",
        notes: "Signs out every other device.",
      },
      {
        method: "GET",
        path: "/api/auth/sessions",
        summary: "Devices this account is signed in on.",
        auth: "user",
        returns: "Session[]",
      },
      {
        method: "DELETE",
        path: "/api/auth/sessions",
        summary: "Sign out everywhere else.",
        auth: "user",
        returns: "204",
      },
      {
        method: "DELETE",
        path: "/api/auth/sessions/:familyId",
        summary: "Sign out one device.",
        auth: "user",
        returns: "204",
      },
    ],
  },
  {
    id: "rooms",
    title: "Rooms",
    intro: `A room is the durable half of a meeting: a stable code, an owner and a set
of switches. It outlives any one call, so a team can bookmark one and use it every
week. The code is the whole URL path, which is why \`/:slug\` is the last route the
client registers.`,
    endpoints: [
      {
        method: "GET",
        path: "/api/rooms",
        summary: "Rooms you own.",
        auth: "user",
        returns: "Room[]",
      },
      {
        method: "POST",
        path: "/api/rooms",
        summary: "Create a room.",
        auth: "user",
        body: `{ "title"?: string, "settings"?: Partial<RoomSettings>, "passcode"?: string }`,
        returns: "Room",
      },
      {
        method: "GET",
        path: "/api/rooms/:slug/preview",
        summary: "What a join screen may know before anybody signs in.",
        auth: "public",
        returns: `{ slug, title, allowGuests, lobby, requiresPasscode, live, participantCount, hostName }`,
        notes: "Deliberately says nothing about who is inside.",
      },
      {
        method: "GET",
        path: "/api/rooms/:slug",
        summary: "A room, with its settings.",
        auth: "user",
        returns: "Room",
      },
      {
        method: "PATCH",
        path: "/api/rooms/:id",
        summary: "Rename a room or change its settings.",
        auth: "user",
        body: `{ "title"?: string, "settings"?: Partial<RoomSettings> }`,
        returns: "Room",
        notes: "A live room obeys the change at once.",
      },
      {
        method: "DELETE",
        path: "/api/rooms/:id",
        summary: "Delete a room.",
        auth: "user",
        returns: "204",
        notes: "Soft, so a code is never reissued.",
      },
      {
        method: "GET",
        path: "/api/rooms/:id/participants",
        summary: "Who is in the call now.",
        auth: "user",
        returns: "Participant[]",
      },
    ],
  },
  {
    id: "meetings",
    title: "Meetings and invitations",
    intro: `A meeting is a planned occurrence pointing at a room. Times are RFC 3339
instants with the organiser's IANA zone beside them, because "every Tuesday at 09:00
in Brussels" is not a fixed offset from UTC.

Each invitee gets a link with a token of their own, so accepting and declining are
attributable without asking anybody to sign in. The token is returned to the
organiser and to nobody else.`,
    endpoints: [
      {
        method: "GET",
        path: "/api/meetings",
        summary: "Your meetings, and ones you were invited to.",
        auth: "user",
        returns: `{ items: Meeting[], nextCursor: null }`,
        notes: "Windowed with ?from= and ?to=.",
      },
      {
        method: "POST",
        path: "/api/meetings",
        summary: "Schedule a meeting.",
        auth: "user",
        body: `{ "title", "description"?, "startsAt", "endsAt", "timezone", "recurrence"?, "roomId"?, "invitees"? }`,
        returns: "Meeting",
        notes: "Mints a room unless roomId names one you own.",
      },
      {
        method: "GET",
        path: "/api/meetings/:id",
        summary: "One meeting.",
        auth: "user",
        returns: "Meeting",
      },
      {
        method: "PATCH",
        path: "/api/meetings/:id",
        summary: "Change a meeting.",
        auth: "user",
        body: "Any subset of the create body",
        returns: "Meeting",
      },
      {
        method: "DELETE",
        path: "/api/meetings/:id",
        summary: "Cancel a meeting.",
        auth: "user",
        returns: "204",
        notes: "The status changes; the row stays, so an invitee is told it was cancelled.",
      },
      {
        method: "POST",
        path: "/api/meetings/:id/invites",
        summary: "Invite addresses.",
        auth: "user",
        body: `{ "emails": string[], "role"?: Role }`,
        returns: "Invitee[]",
      },
      {
        method: "DELETE",
        path: "/api/meetings/:id/invites/:inviteId",
        summary: "Withdraw an invitation.",
        auth: "user",
        returns: "204",
      },
      {
        method: "GET",
        path: "/api/invites/:token",
        summary: "Read an invitation.",
        auth: "public",
        returns: `{ meeting, invitee }`,
      },
      {
        method: "POST",
        path: "/api/invites/:token/respond",
        summary: "Accept or decline.",
        auth: "public",
        body: `{ "status": "accepted" | "declined" }`,
        returns: `{ meeting, invitee }`,
      },
    ],
  },
  {
    id: "boards",
    title: "Presenting",
    intro: `An upload returns as soon as the file has landed, with the board in
\`converting\`. The pages arrive later over the socket, because rendering two hundred
slides takes minutes and nothing should hold a request open for that.

Documents become webp slides through libreoffice, poppler and cwebp. Images are
encoded inline. A blank whiteboard needs none of them.`,
    endpoints: [
      {
        method: "POST",
        path: "/api/rooms/:slug/boards",
        summary: "Upload a document, images, or open a blank whiteboard.",
        auth: "public",
        body: 'The raw file, with ?filename= and ?title=; or JSON { kind: "blank", title, pages }',
        returns: "Board (202 while converting, 201 when ready)",
        notes: "Pass ?socket= to receive board:progress on that connection.",
      },
      {
        method: "GET",
        path: "/api/rooms/:slug/boards/hash/:hash",
        summary: "The board this meeting already made from that file, if it has one.",
        auth: "public",
        returns: "Board, or 404 when this meeting has never been sent that file",
        notes: `:hash is the SHA-256 of the file, lowercase hex. Ask before uploading: a
hit is a board to open rather than megabytes to send and a document to render twice.
The server hashes what actually arrives, so an upload of a file it already holds
answers with the existing board too.`,
      },
      {
        method: "GET",
        path: "/api/boards/:id",
        summary: "A board and its pages.",
        auth: "public",
        returns: "Board",
      },
      {
        method: "DELETE",
        path: "/api/boards/:id",
        summary: "Delete a board and its files.",
        auth: "user",
        returns: "204",
      },
      {
        method: "GET",
        path: "/cdn/boards/:boardId/:page",
        summary: "One rendered slide.",
        auth: "public",
        returns: "image/webp",
      },
    ],
  },
  {
    id: "meta",
    title: "Instance and media",
    intro: `What this deployment allows, and the ICE servers a call needs. The ICE
credential is short lived by design: it carries its own expiry and the relay verifies
it without this server and the relay sharing per-user state.`,
    endpoints: [
      {
        method: "GET",
        path: "/healthz",
        summary: "Liveness. 503 when the database is unreachable.",
        auth: "public",
        returns: `{ ok, version, connections, boardQueue, time }`,
      },
      {
        method: "GET",
        path: "/api/config",
        summary: "Features and limits.",
        auth: "public",
        returns: `{ appName, registrationOpen, guestsAllowed, features, limits, boardMimeTypes }`,
      },
      {
        method: "GET",
        path: "/api/ice",
        summary: "STUN and TURN servers.",
        auth: "public",
        returns: `{ iceServers, sfuAvailable }`,
      },
      {
        method: "GET",
        path: "/api/version",
        summary: "Build and what is configured.",
        auth: "public",
        returns: `{ name, version, sfu, turn, queue }`,
      },
    ],
  },
];

export const SOCKET: Section = {
  id: "socket",
  title: "The WebSocket protocol",
  intro: `One frame shape in both directions: \`{ "t": event, "d": payload }\`. A frame
that wants an answer adds \`"i"\`, an integer the server echoes on its reply; a reply
that failed carries \`"e": true\` and \`d\` becomes \`{ code, message }\`. That is the
whole framing.

Connect to \`wss://<api-origin>/ws\`, send \`session:auth\` first, and wait for
\`session:ready\` before anything else. A connection that never authenticates is
dropped after ten seconds.`,
  code: [
    {
      caption: "Opening a connection as a guest",
      language: "javascript",
      source: `const socket = new WebSocket("wss://meet-api.bsafegroup.be/ws");

socket.addEventListener("open", () => {
  socket.send(JSON.stringify({
    t: "session:auth",
    d: { protocol: "bsmeet/1", guestName: "Sara" },
  }));
});

socket.addEventListener("message", (event) => {
  const frame = JSON.parse(event.data);
  if (frame.t === "session:ready") {
    socket.send(JSON.stringify({
      t: "room:join",
      d: { slug: "abcd-efgh-ijkl", mic: true, cam: true },
      i: 1,
    }));
  }
});`,
    },
  ],
  events: [
    {
      name: "session:auth",
      direction: "in",
      payload: `{ protocol, accessToken? | guestName? }`,
      summary: "First frame. A token signs you in; a name joins as a guest.",
    },
    {
      name: "session:ping",
      direction: "in",
      payload: `{ ts }`,
      summary: "Liveness probe; answered with session:pong.",
    },
    {
      name: "session:ready",
      direction: "out",
      payload: `{ protocol, userId, displayName, heartbeatIntervalMs, serverTime }`,
      summary: "Authenticated. Nothing else is accepted before this.",
    },
    {
      name: "session:closed",
      direction: "out",
      payload: `{ code, reason, fatal }`,
      summary: "Why the connection is about to end. `fatal` means retrying will not help.",
    },
    {
      name: "room:join",
      direction: "in",
      payload: `{ slug, passcode?, mic, cam }`,
      summary: "Enter a room. Answers room:state, or puts you in the lobby.",
    },
    { name: "room:leave", direction: "in", payload: `{}`, summary: "Leave." },
    {
      name: "room:state",
      direction: "out",
      payload: `{ room, self, participants, lobby, producers, board, strokes, chat, recording }`,
      summary: "Everything needed to draw the room, in one frame.",
    },
    {
      name: "room:settings",
      direction: "out",
      payload: "RoomSettings",
      summary: "A host changed something.",
    },
    {
      name: "room:ended",
      direction: "out",
      payload: `{ reason }`,
      summary: "The host ended it for everybody.",
    },
    {
      name: "participant:joined",
      direction: "out",
      payload: "Participant",
      summary: "Somebody arrived.",
    },
    {
      name: "participant:updated",
      direction: "out",
      payload: "Participant",
      summary: "Their mic, camera, hand or role changed.",
    },
    {
      name: "participant:left",
      direction: "out",
      payload: `{ participantId }`,
      summary: "Somebody left.",
    },
    {
      name: "media:state",
      direction: "in",
      payload: `{ mic?, cam?, screen? }`,
      summary: "What you are publishing.",
    },
    {
      name: "media:hand",
      direction: "in",
      payload: `{ raised }`,
      summary: "Raise or lower your hand.",
    },
    {
      name: "lobby:waiting",
      direction: "out",
      payload: `{ entries }`,
      summary: "Who is knocking. Sent to hosts and co-hosts.",
    },
    {
      name: "lobby:admitted",
      direction: "out",
      payload: `{}`,
      summary: "You were let in; re-send room:join.",
    },
    {
      name: "lobby:denied",
      direction: "out",
      payload: `{ reason }`,
      summary: "You were not let in.",
    },
    {
      name: "sfu:capabilities",
      direction: "in",
      payload: `{}`,
      summary: "The router's RTP capabilities.",
    },
    {
      name: "sfu:transport-create",
      direction: "in",
      payload: `{ direction: "send" | "recv" }`,
      summary: "One direction of your media.",
    },
    {
      name: "sfu:transport-connect",
      direction: "in",
      payload: `{ transportId, dtlsParameters }`,
      summary: "Complete the DTLS handshake.",
    },
    {
      name: "sfu:produce",
      direction: "in",
      payload: `{ transportId, kind, source, rtpParameters }`,
      summary: "Start publishing a track.",
    },
    {
      name: "sfu:consume-many",
      direction: "in",
      payload: `{ transportId, rtpCapabilities, producers }`,
      summary: "Subscribe to a whole set at once, each with its starting layer.",
    },
    {
      name: "sfu:consumers-resume",
      direction: "in",
      payload: `{ consumerIds }`,
      summary: "Start everything consume-many created.",
    },
    {
      name: "sfu:consumer-layers",
      direction: "in",
      payload: `{ layers }`,
      summary: "Re-aim subscriptions after a layout change.",
    },
    {
      name: "producer:added",
      direction: "out",
      payload: "ProducerInfo",
      summary: "Somebody started a track. Consume it.",
    },
    {
      name: "producer:removed",
      direction: "out",
      payload: `{ producerId }`,
      summary: "A track stopped.",
    },
    {
      name: "chat:send",
      direction: "in",
      payload: `{ body }`,
      summary: "Send a message to the room.",
    },
    {
      name: "chat:message",
      direction: "out",
      payload: "ChatMessage",
      summary: "Echoed to everybody, sender included.",
    },
    {
      name: "board:open",
      direction: "in",
      payload: `{ boardId }`,
      summary: "Put a board on everybody's screen.",
    },
    {
      name: "board:page",
      direction: "in",
      payload: `{ page }`,
      summary: "Everybody follows the presenter.",
    },
    {
      name: "board:stroke",
      direction: "in",
      payload: "BoardStroke",
      summary: "One drawing operation, in 0..1 page coordinates.",
    },
    {
      name: "board:progress",
      direction: "out",
      payload: `{ boardId, page, totalPages }`,
      summary: "Conversion progress, to the uploader only.",
    },
    { name: "board:ready", direction: "out", payload: "Board", summary: "The pages are rendered." },
    {
      name: "mod:role",
      direction: "in",
      payload: `{ participantId, role }`,
      summary: "Promote or demote. Rank decides what is allowed.",
    },
    {
      name: "mod:mute",
      direction: "in",
      payload: `{ participantId }`,
      summary: "Mute one person.",
    },
    {
      name: "mod:remove",
      direction: "in",
      payload: `{ participantId }`,
      summary: "Remove one person.",
    },
    {
      name: "mod:lobby-admit",
      direction: "in",
      payload: `{ participantId }`,
      summary: "Let somebody in.",
    },
    {
      name: "mod:end",
      direction: "in",
      payload: `{}`,
      summary: "End the meeting for everybody. Host only.",
    },
  ],
};

export const INTEGRATION: Section = {
  id: "integration",
  title: "Integrating BS Meet",
  intro: `This is how BS Chat and BS Calendar embed BS Meet. A service key
authenticates the calling server, not a user, and is sent as \`X-BSMeet-Key\`. Mint
one on the API host with \`bsmeet-server --mint-key <service> "<label>"\`; the
plaintext is printed once and stored only as a hash.

The model deliberately does not require your users to have BS Meet accounts. A room
is created against an address, and one is provisioned if that address has none, so
the person owns their own rooms rather than your service owning them. A join token
puts one of your users into one room, under a name you choose, for five minutes,
with no account at all.

Six room settings decide what an embedded call feels like, and they are worth
setting deliberately rather than inheriting. \`stage\` picks the template - \`standard\`
is the meeting grid, \`telegram\` is the talk layout built for a phone. \`autoJoin\`
skips the camera-and-microphone screen, which is right when the person already
pressed a button in your product that said "join the call". \`muteOnEntry\` and
\`cameraOffOnEntry\` decide what they arrive with. \`guestMic\`, \`guestCam\` and
\`guestShare\` decide whether an ordinary arrival may open anything at all, which is
how a public room keeps an audience quiet until a moderator says otherwise - and
they apply to the \`guest\` role only, so send your own moderators in as \`cohost\`.
\`shareLink\` off hides the room code, for a room whose guest list is settled in
your product.`,
  endpoints: [
    {
      method: "POST",
      path: "/api/service/rooms",
      summary: "Create a room on behalf of one of your users.",
      auth: "service",
      body: `{ "ownerEmail", "ownerName"?, "title"?, "settings"?, "passcode"? }`,
      returns: `{ id, slug, title, joinUrl, settings, live, participantCount }`,
    },
    {
      method: "GET",
      path: "/api/service/rooms/:slug",
      summary: "Look a room up.",
      auth: "service",
      returns: "Room",
    },
    {
      method: "PATCH",
      path: "/api/service/rooms/:slug",
      summary: "Change the settings of a room you made.",
      auth: "service",
      body: `{ "settings": Partial<RoomSettings> }`,
      returns: "Room",
      notes: `Merged over the room's current values, so you state the one switch you
care about. It exists because a room is made once and reused for years: without it, a
setting added after you created your rooms would never reach any of them. Restating
your settings at the start of each call is cheap and idempotent.`,
    },
    {
      method: "POST",
      path: "/api/service/rooms/:slug/end",
      summary: "Stop the call in a room now.",
      auth: "service",
      body: `{ "reason"?: string }`,
      returns: `{ slug, ended }`,
      notes: `Everybody is disconnected and the recording is stopped. Your service is
the only side that knows what "everybody has left" means - BS Meet sees sockets, you
see people. A room with no call in it answers 200 with \`ended: false\`, because the
caller asked for the call to be over and it is.`,
    },
    {
      method: "POST",
      path: "/api/service/meetings",
      summary: "Schedule a meeting from your calendar.",
      auth: "service",
      body: `{ "externalId", "externalKind"?, "title", "organizerEmail", "startsAt", "endsAt", "timezone"?, "inviteeEmails"?, "settings"? }`,
      returns: "Meeting",
      notes: `Idempotent on externalId, so a retried webhook does not create two - settings
are only applied the first time a meeting is created, same as the room they open.`,
    },
    {
      method: "GET",
      path: "/api/service/meetings/:externalId",
      summary: "Find a meeting by your own id.",
      auth: "service",
      returns: "Meeting",
    },
    {
      method: "DELETE",
      path: "/api/service/meetings/:externalId",
      summary: "Cancel it.",
      auth: "service",
      returns: "204",
    },
    {
      method: "POST",
      path: "/api/service/join-tokens",
      summary: "Let one of your users into one room.",
      auth: "service",
      body: `{ "roomSlug", "displayName", "externalUserId"?, "role"?: "guest" | "presenter" | "cohost" }`,
      returns: `{ joinToken, roomSlug, joinUrl, expiresIn }`,
      notes: `Five minutes, one room, and a separate audience so it cannot be used as a
sign-in. It works in a room with guests turned off, because your service key is the vouch.
A cohost goes straight in; a guest or presenter still waits where the room has a lobby,
since vouching for somebody is not the host agreeing to see them.`,
    },
  ],
  code: [
    {
      caption: "Putting a join button in a BS Chat conversation",
      language: "javascript",
      source: `// 1. Once per conversation: a room that keeps its code.
const room = await fetch("https://meet-api.bsafegroup.be/api/service/rooms", {
  method: "POST",
  headers: { "Content-Type": "application/json", "X-BSMeet-Key": KEY },
  body: JSON.stringify({
    ownerEmail: conversation.owner.email,
    ownerName: conversation.owner.name,
    title: conversation.title,
  }),
}).then((r) => r.json());

// 2. Per person, per click: a link good for five minutes.
const ticket = await fetch("https://meet-api.bsafegroup.be/api/service/join-tokens", {
  method: "POST",
  headers: { "Content-Type": "application/json", "X-BSMeet-Key": KEY },
  body: JSON.stringify({
    roomSlug: room.slug,
    displayName: member.name,
    externalUserId: member.id,
    role: member.isModerator ? "cohost" : "guest",
  }),
}).then((r) => r.json());

// 3. Send them there. No BS Meet account was created.
window.open(ticket.joinUrl, "_blank");`,
    },
    {
      caption: "Framing a call inside your own product",
      language: "javascript",
      source: `// Five things on the link, none of which is a fact about the room.
// All of them are facts about this person, in this session, right now.
const url = new URL(ticket.joinUrl);          // already carries ?t=<joinToken>
url.searchParams.set("return", RETURN_URL);   // where leaving puts them
url.searchParams.set("theme", "dark");        // whichever your app resolved to
url.searchParams.set("stage", "telegram");    // paints right on the first frame
url.searchParams.set("embed", "myproduct");   // you will catch the messages below

frame.allow = "camera; microphone; display-capture; autoplay; fullscreen";
frame.src = url.toString();

// Changing theme mid-call: the link is spent, so say it again.
frame.contentWindow.postMessage({ source: "bsmeet:theme", theme: "light" }, MEET_ORIGIN);

// And what the call asks of you. It cannot move your panel - it is inside it -
// so its minimise chevron and a drag down its stage arrive here instead.
window.addEventListener("message", (event) => {
  if (event.origin !== MEET_ORIGIN) return;
  if (event.data?.source !== "bsmeet:host") return;
  switch (event.data.type) {
    case "drag":        return panel.follow(event.data.dy);  // pixels, never negative
    case "drag-cancel": return panel.settle();
    case "minimize":    return panel.putAway();
    case "left":        return panel.close();
  }
});

// \`return\` is only honoured for an origin this deployment already trusts, so
// add yours to BSMEET_RETURN_ORIGINS. The theme and host messages are not held
// to that list: one sets a CSS attribute and the others move your own element.`,
    },
  ],
};

export const ERRORS = `Every non-2xx reply has the same body:

    { "error": { "code": "room_full", "message": "This meeting is full", "fields": { } } }

Match on \`code\`, which is stable. \`message\` is written for a person and may change;
\`fields\` appears only on validation failures and maps a field name to its message.
Socket refusals use the same \`code\` and \`message\` inside the reply frame.`;
